/**
 * Parsea un Azure Storage connection string y extrae AccountName y AccountKey.
 */
function parseConnectionString(cs: string): { accountName: string; accountKey: string } {
  const parts: Record<string, string> = {};
  for (const segment of cs.split(';')) {
    const idx = segment.indexOf('=');
    if (idx === -1) continue;
    parts[segment.slice(0, idx)] = segment.slice(idx + 1);
  }
  if (!parts['AccountName'] || !parts['AccountKey']) {
    throw new Error('Connection string inválido: faltan AccountName o AccountKey');
  }
  return { accountName: parts['AccountName'], accountKey: parts['AccountKey'] };
}

/**
 * Genera una SAS URL temporal (lectura, 2 horas) para un blob usando
 * HMAC-SHA256 con la Web Crypto API del navegador.
 */
async function generateBlobSASUrl(
  accountName: string,
  accountKey: string,
  containerName: string,
  blobName: string,
  permissions = 'r'
): Promise<string> {
  const version = '2020-12-06';
  const now = new Date();
  const start = new Date(now.getTime() - 5 * 60 * 1000);   // 5 min atrás
  const expiry = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 horas

  const fmt = (d: Date) => d.toISOString().replace(/\.\d{3}Z$/, 'Z');
  const startStr = fmt(start);
  const expiryStr = fmt(expiry);

  // Formato requerido por Azure Storage REST API (versión 2020-12-06)
  const canonicalizedResource = `/blob/${accountName}/${containerName}/${blobName}`;
  const stringToSign = [
    permissions,            // signedPermissions
    startStr,               // signedStart
    expiryStr,              // signedExpiry
    canonicalizedResource,  // canonicalizedResource
    '',                     // signedIdentifier
    '',                     // signedIP
    '',                     // signedProtocol
    version,                // signedVersion
    'b',                    // signedResource (b = blob)
    '',                     // signedSnapshotTime
    '',                     // signedEncryptionScope
    '',                     // rscc
    '',                     // rscd
    '',                     // rsce
    '',                     // rscl
    '',                     // rsct
  ].join('\n');

  const keyBytes = Uint8Array.from(atob(accountKey), (c) => c.charCodeAt(0));
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const sigBytes = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    new TextEncoder().encode(stringToSign)
  );
  const sigBase64 = btoa(String.fromCharCode(...new Uint8Array(sigBytes)));

  const qs = new URLSearchParams({
    sv: version,
    st: startStr,
    se: expiryStr,
    sr: 'b',
    sp: permissions,
    sig: sigBase64,
  });

  const encodedBlobPath = blobName
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

  return `https://${accountName}.blob.core.windows.net/${containerName}/${encodedBlobPath}?${qs.toString()}`;
}

function getAccountCredentialsFromConnectionString(connectionString: string): { accountName: string; accountKey: string } {
  if (!connectionString) {
    throw new Error('Connection string vacío.');
  }
  return parseConnectionString(connectionString);
}

export async function getBlobUrlByConnectionString(
  connectionString: string,
  containerName: string,
  blobName: string,
  permissions: string = 'r'
): Promise<string> {
  const { accountName, accountKey } = getAccountCredentialsFromConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, permissions);
}

/**
 * Devuelve la URL SAS temporal para el CSV configurado en .env
 */
export async function getBlobCSVUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const csvFileName = process.env.REACT_APP_SM_BLOB_CSV_FILE as string;

  if (!connectionString || !containerName || !csvFileName) {
    throw new Error('Faltan variables de entorno: REACT_APP_SM_BLOB_CONNECTION_STRING, REACT_APP_SM_BLOB_CONTAINER_NAME o REACT_APP_SM_BLOB_CSV_FILE');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, csvFileName, 'r');
}

/**
 * Devuelve la URL SAS temporal para escritura del CSV configurado en .env.
 */
export async function getBlobCSVWriteUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const csvFileName = process.env.REACT_APP_SM_BLOB_CSV_FILE as string;

  if (!connectionString || !containerName || !csvFileName) {
    throw new Error('Faltan variables de entorno para escritura CSV.');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, csvFileName, 'rw');
}

function buildPromptTxtBlobName(promptFileName: string): string {
  const normalizedName = String(promptFileName || '').trim().replace(/^\/+/, '');

  if (!normalizedName) {
    throw new Error('El nombre del archivo de prompt está vacío.');
  }

  if (normalizedName.includes('/')) {
    return /\.(txt|md)$/i.test(normalizedName)
      ? normalizedName
      : `${normalizedName}.txt`;
  }

  const fileName = /\.(txt|md)$/i.test(normalizedName)
    ? normalizedName
    : `${normalizedName}.txt`;

  return `desarrollo/${fileName}`;
}

/**
 * Devuelve la URL SAS temporal para el archivo .txt de prompts ("promptsprocesos/desarrollo/nombre.txt")
 */
export async function getBlobTxtUrl(promptFileName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const txtFileName = buildPromptTxtBlobName(promptFileName);

  if (!connectionString) {
    throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING en el entorno');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, txtFileName, 'r');
}

/**
 * Devuelve la URL SAS temporal para escritura del archivo .txt del servicio.
 */
export async function getBlobTxtWriteUrl(promptFileName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const txtFileName = buildPromptTxtBlobName(promptFileName);

  if (!connectionString) {
    throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING en el entorno');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, txtFileName, 'rw');
}

/**
 * URL SAS temporal para escritura del archivo .txt del servicio (SECUNDARIO).
 */
export async function getSecondaryBlobTxtWriteUrl(promptFileName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const txtFileName = buildPromptTxtBlobName(promptFileName);

  if (!connectionString) {
    throw new Error('Falta REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, txtFileName, 'rw');
}

/**
 * Ruta del historial de un archivo de instrucciones dentro de "promptsprocesos":
 * historial/{ID}/{nombre}/{marca}.md. Es la misma ruta en la que guardan su
 * historial los agentes cuando cambian sus instrucciones por chat.
 */
export function buildPromptHistoryBlobName(serviceId: string, fileName: string, timestamp: string): string {
  const id = String(serviceId || '').trim().replace(/^\/+|\/+$/g, '');
  const name = String(fileName || '').trim().replace(/^\/+|\/+$/g, '');
  const stamp = String(timestamp || '').trim();

  if (!id || !name || !stamp) {
    throw new Error('Faltan el id del servicio, el nombre del archivo o la marca de tiempo para el historial.');
  }

  return `historial/${id}/${name}/${stamp}.md`;
}

/**
 * URL SAS temporal para escribir una versión anterior de un archivo de instrucciones
 * en el historial (solo cuenta PRINCIPAL).
 */
export async function getBlobPromptHistoryWriteUrl(
  serviceId: string,
  fileName: string,
  timestamp: string
): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const blobName = buildPromptHistoryBlobName(serviceId, fileName, timestamp);

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'rw');
}

/**
 * Devuelve URL SAS temporal de escritura para prompt txt usando un connection string específico.
 */
export async function getBlobTxtWriteUrlByConnectionString(
  serviceName: string,
  connectionString: string,
  containerName = 'promptsprocesos',
  folderName = 'desarrollo'
): Promise<string> {
  const txtFileName = `${folderName}/${serviceName}.txt`;
  const { accountName, accountKey } = getAccountCredentialsFromConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, txtFileName, 'rw');
}

/**
 * Devuelve la URL SAS temporal para lectura del directorio JSON de usuarios.
 */
export async function getDirectoryJsonUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_DIRECTORY_CONTAINER_NAME as string;
  const directoryFileName = process.env.REACT_APP_SM_DIRECTORY_JSON_FILE as string;

  if (!connectionString || !containerName || !directoryFileName) {
    throw new Error('Faltan variables de entorno para el directorio JSON.');
  }

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, directoryFileName, 'r');
}

/**
 * URL SAS para leer el CSV de variables (lista_variables_servicios.csv)
 */
export async function getBlobVariablesCsvUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const csvFileName = 'lista_variables_servicios.csv';

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING en el entorno');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, csvFileName, 'r');
}

/**
 * URL SAS para leer la configuración JSON de un servicio.
 */
export async function getBlobServiceConfigUrl(serviceName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'configuraciones';
  const jsonFileName = `${serviceName}.json`;

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING en el entorno');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, jsonFileName, 'r');
}

/**
 * URL SAS temporal para escritura de la configuración JSON de un servicio.
 */
export async function getBlobServiceConfigWriteUrl(serviceName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'configuraciones';
  const jsonFileName = `${serviceName}.json`;

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, jsonFileName, 'rw');
}

/**
 * URL SAS temporal para escritura de la configuración JSON de un servicio (SECUNDARIO).
 */
export async function getSecondaryBlobServiceConfigWriteUrl(serviceName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = 'configuraciones';
  const jsonFileName = `${serviceName}.json`;

  if (!connectionString) throw new Error('Falta REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, jsonFileName, 'rw');
}

/**
 * URL SAS temporal para escritura del archivo CSV de variables (lista_variables_servicios.csv).
 */
export async function getBlobVariablesCsvWriteUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const csvFileName = 'lista_variables_servicios.csv';

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING en el entorno');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, csvFileName, 'rw');
}

/**
 * URL SAS temporal para escritura del archivo CSV de variables (SECUNDARIO).
 */
export async function getSecondaryBlobVariablesCsvWriteUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = 'promptsprocesos';
  const csvFileName = 'lista_variables_servicios.csv';

  if (!connectionString) throw new Error('Falta REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, csvFileName, 'rw');
}

/**
 * URL SAS temporal para escritura de un nuevo archivo PDF anónimo.
 */
export async function getBlobPdfWriteUrl(folderName: string, fileName: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;
  const containerName = 'pdfsceu';
  const blobName = `${folderName}/${fileName}`;

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING');

  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'rw');
}

/**
 * URL SAS temporal para borrar un PDF existente a partir de su URL pública.
 */
export async function getBlobPdfDeleteUrl(blobUrl: string): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_BLOB_CONNECTION_STRING as string;

  if (!connectionString) throw new Error('Falta REACT_APP_SM_BLOB_CONNECTION_STRING');

  const parsedUrl = new URL(blobUrl);
  const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
  const [containerName, ...blobPathSegments] = pathSegments;

  if (!containerName || !blobPathSegments.length) {
    throw new Error('La ruta del blob PDF no es válida.');
  }

  const blobName = blobPathSegments.map((segment) => decodeURIComponent(segment)).join('/');
  const { accountName, accountKey } = parseConnectionString(connectionString);

  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'd');
}

/**
 * URL SAS temporal para lectura del archivo de instrucciones del editor (prompt_editor.md)
 * desde la cuenta SECUNDARIA.
 */
export async function getBlobPromptEditorUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const folderName = process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string;

  if (!connectionString || !containerName || !folderName) {
    throw new Error('Faltan variables de entorno: REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING, REACT_APP_SM_BLOB_CONTAINER_NAME o REACT_APP_SM_PROMPTS_FOLDER_NAME');
  }

  const blobName = `${folderName}/prompt_editor.md`;
  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'r');
}

/**
 * URL SAS temporal para escritura del archivo de instrucciones del editor (prompt_editor.md)
 * en la cuenta SECUNDARIA.
 */
export async function getBlobPromptEditorWriteUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const folderName = process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string;

  if (!connectionString || !containerName || !folderName) {
    throw new Error('Faltan variables de entorno para escritura de prompt_editor.md');
  }

  const blobName = `${folderName}/prompt_editor.md`;
  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'rw');
}

/**
 * URL SAS temporal para lectura del archivo de instrucciones del editor de agentes de agent-factory
 * (prompt_editor_agentes.md) desde la cuenta SECUNDARIA.
 */
export async function getBlobAgentPromptEditorUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const folderName = process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string;

  if (!connectionString || !containerName || !folderName) {
    throw new Error('Faltan variables de entorno: REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING, REACT_APP_SM_BLOB_CONTAINER_NAME o REACT_APP_SM_PROMPTS_FOLDER_NAME');
  }

  const blobName = `${folderName}/prompt_editor_agentes.md`;
  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'r');
}

/**
 * URL SAS temporal para escritura del archivo de instrucciones del editor de agentes de agent-factory
 * (prompt_editor_agentes.md) en la cuenta SECUNDARIA.
 */
export async function getBlobAgentPromptEditorWriteUrl(): Promise<string> {
  const connectionString = process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING as string;
  const containerName = process.env.REACT_APP_SM_BLOB_CONTAINER_NAME as string;
  const folderName = process.env.REACT_APP_SM_PROMPTS_FOLDER_NAME as string;

  if (!connectionString || !containerName || !folderName) {
    throw new Error('Faltan variables de entorno para escritura de prompt_editor_agentes.md');
  }

  const blobName = `${folderName}/prompt_editor_agentes.md`;
  const { accountName, accountKey } = parseConnectionString(connectionString);
  return generateBlobSASUrl(accountName, accountKey, containerName, blobName, 'rw');
}

